<template>
  <el-dialog
    :title="title"
    :visible="visible"
    width="480px"
    @update:visible="$emit('update:visible', $event)"
    @open="reset"
  >
    <div class="ph__current">
      Trenutna faza: <StatusTag :value="currentStatus" pill />
      <span v-if="reopening" class="ph__reopen-note">predmet je zatvoren</span>
    </div>

    <div v-if="reopening" class="ph__notice">
      <i class="el-icon-refresh-left"></i>
      Zatvoren predmet se ponovo otvara. U tok predmeta se upisuje
      <strong>„Ponovo otvoreno”</strong>, pa nova faza.
    </div>

    <div class="ph__list">
      <button
        v-for="opt in phases"
        :key="opt.id"
        type="button"
        class="ph__opt"
        :class="{
          'is-selected': form.status === opt.id,
          'ph__opt--terminal': opt.id === CLOSED_ID,
        }"
        :disabled="opt.id === currentStatus"
        @click="pick(opt.id)"
      >
        <span class="ph__dot" :style="{ background: opt.dot }"></span>
        <span class="ph__name">{{ opt.label }}</span>
        <span v-if="opt.id === currentStatus" class="ph__badge">trenutno</span>
        <i v-else-if="form.status === opt.id" class="el-icon-check ph__check"></i>
      </button>
    </div>

    <div v-if="form.status === CLOSED_ID" class="ph__field">
      <label>Razlog zatvaranja <span class="req">*</span></label>
      <el-select class="w100" v-model="form.statusReason" placeholder="Odaberite razlog">
        <el-option
          v-for="item in reasonOptions"
          :key="item.value"
          :label="item.label"
          :value="item.value"
        ></el-option>
      </el-select>
    </div>

    <div class="ph__field">
      <label>Komentar</label>
      <el-input
        v-model="form.comment"
        placeholder="Zašto se faza mijenja — vidljivo u toku predmeta"
        autocomplete="off"
      ></el-input>
    </div>

    <div v-if="error" class="dlg__err">{{ error }}</div>

    <span slot="footer" class="dialog-footer">
      <el-button @click="$emit('update:visible', false)">Odustani</el-button>
      <el-button type="primary" :loading="saving" @click="submit">
        {{ reopening ? "Ponovo otvori" : "Potvrdi promjenu" }}
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
import API from "@/services/api/api";
import StatusTag from "@/components/StatusTag";
import { selectablePhases, REOPENED_ID, CLOSED_ID } from "@/models/status";

export default {
  name: "PhaseDialog",
  components: { StatusTag },
  props: {
    visible: { type: Boolean, default: false },
    predmetId: { type: Number, default: 0 },
    predmetBroj: { type: String, default: "" },
    currentStatus: { type: Number, default: -1 },
    // Admin mode allows moving a closed case, which normal users cannot do.
    admin: { type: Boolean, default: false },
  },
  data() {
    return {
      CLOSED_ID,
      form: {},
      error: "",
      saving: false,
      reasonOptions: [
        { value: 1, label: "Neosnovano" },
        { value: 2, label: "Odustali" },
        { value: 3, label: "Završen" },
        { value: 4, label: "Drugo" },
      ],
    };
  },
  computed: {
    phases() {
      return selectablePhases();
    },
    // Moving a closed case back into the workflow is a reopen.
    reopening() {
      return this.admin && this.currentStatus === CLOSED_ID;
    },
    title() {
      const suffix = this.predmetBroj ? " — " + this.predmetBroj : "";
      return (this.reopening ? "Ponovo otvori predmet" : "Promijeni fazu") + suffix;
    },
  },
  methods: {
    reset() {
      this.form = {};
      this.error = "";
    },
    pick(id) {
      this.$set(this.form, "status", id);
      this.error = "";
    },
    async submit() {
      if (typeof this.form.status == "undefined") {
        this.error = "Odaberite novu fazu.";
        return;
      }
      if (this.form.status === CLOSED_ID && typeof this.form.statusReason == "undefined") {
        this.error = "Odaberite razlog zatvaranja.";
        return;
      }
      this.error = "";
      this.saving = true;
      try {
        // Reopening writes its own history entry first, so the case's timeline
        // shows that it was reopened rather than silently jumping phase.
        if (this.reopening) {
          await API.Generic.update("predmet/status", {
            predmet_id: this.predmetId,
            status_id: REOPENED_ID,
            comment: this.form.comment || "Predmet ponovo otvoren",
            reason: 0,
          });
        }
        await API.Generic.update("predmet/status", {
          predmet_id: this.predmetId,
          status_id: this.form.status,
          comment: this.form.comment || "",
          reason: this.form.status === CLOSED_ID ? this.form.statusReason : 0,
        });
        this.$emit("update:visible", false);
        this.$emit("updated");
      } catch (e) {
        this.error = "Faza nije promijenjena — pokušajte ponovo.";
        console.log(e);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.ph__current {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: var(--fs-sm);
  color: var(--ink-3);
  margin-bottom: 14px;
}

.ph__reopen-note {
  color: var(--ink-4);
}

.ph__notice {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: var(--brand-tint);
  border: 1px solid var(--brand-line);
  color: var(--brand-ink);
  border-radius: var(--r-md);
  padding: 10px 12px;
  font-size: var(--fs-sm);
  line-height: 1.5;
  margin-bottom: 14px;
}

.ph__list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 16px;
}

.ph__opt {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 9px 12px;
  background: none;
  border: 1px solid transparent;
  border-radius: var(--r-md);
  font: inherit;
  font-size: var(--fs-md);
  color: var(--ink);
  text-align: left;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: var(--surface-alt);
  }

  &.is-selected {
    background: var(--brand-tint);
    border-color: var(--brand-line);
    color: var(--brand-ink);
    font-weight: 600;
  }

  &:disabled {
    cursor: default;
    color: var(--ink-4);
  }
}

.ph__opt--terminal {
  margin-top: 6px;
  border-top: 1px solid var(--line-soft);
  border-radius: 0 0 var(--r-md) var(--r-md);
  padding-top: 12px;
}

.ph__dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  flex-shrink: 0;
}

.ph__name {
  flex-grow: 1;
}

.ph__badge {
  font-size: var(--fs-xs);
  color: var(--ink-4);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.ph__check {
  color: var(--brand);
  font-weight: 700;
}

.ph__field {
  margin-bottom: 14px;

  label {
    display: block;
    font-size: var(--fs-sm);
    color: var(--ink-2);
    margin-bottom: 5px;
  }
}

.dlg__err {
  font-size: var(--fs-sm);
  color: var(--danger);
  margin-bottom: 6px;
}

.req {
  color: var(--danger);
}

.w100 {
  width: 100%;
}
</style>
